import { useState, useEffect } from "react";
import { useFormContext } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { RootState } from "@/redux/store";
import { Card, CardContent } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useGetMealPlansQuery } from "@/services/mealPlansApi";
import { Consultation, HistoryConsultation } from "@/types/consultation";
import { cn } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";
import { HistoryFilter } from "../history-filter";
import { visibleFor } from "@/lib/constants/meal_plans";
import usePagination from "@/hooks/use-pagination";
import Pagination from "@/components/ui/table/pagination-table";
import TableSearchFilter from "@/components/ui/table/data-table-search";
import useTableData from "@/components/ui/table/data-table-logic";
import { Button } from "@/components/ui/button";
import { PlusIcon } from "lucide-react";

interface FormStepProps {
  action: "add" | "edit";
  open: boolean;
}

interface SearchCriteriaType {
    limit: number;
    offset: number;
    sort_key: string;
    sort_order: string;
    search_key?: string;
    [key: string]: string | number | (string | number)[] | null | undefined;
}

const initialValue: SearchCriteriaType = {
  limit: 10,
  offset: 0,
  sort_order: "desc",
  sort_key: "id",
};

const MealPlanStep = ({ action, open }: FormStepProps) => {
    const { setValue, watch, getValues } = useFormContext<Consultation & { assigned_meal_plan_ids?: number[] }>();
    const navigate = useNavigate();
    const orgId = useSelector((state: RootState) => state.auth.userInfo?.user?.org_id) || 0;
    const [hasAutoSelected, setHasAutoSelected] = useState(false);
    
    // Table Data Logic (State for Search & Criteria)
    const {
        searchCriteria,
        setSearchCriteria,
        query,
        inputValue,
        setInputValue,
    } = useTableData<SearchCriteriaType>(initialValue);

    // Fetch Meal Plans
    const { data: mealPlansData, isLoading } = useGetMealPlansQuery({ org_id: Number(orgId), query: query }, { skip: !open });
    
    // Selection State
    const assignedIds = watch("assigned_meal_plan_ids") || [];
    const memberId = watch("member_id");

    // Auto-select logic
    useEffect(() => {
        if (!isLoading && mealPlansData?.data && !hasAutoSelected && memberId) {
            const preSelectedIds: number[] = [];
            mealPlansData.data.forEach(plan => {
                const rawId = plan.meal_plan_id ?? plan.id;
                const planId = Number(rawId);
                
                if (planId && !Number.isNaN(planId) && plan.member_id && plan.member_id.includes(Number(memberId))) {
                    preSelectedIds.push(planId);
                }
            });

            if (preSelectedIds.length > 0) {
                 if (assignedIds.length === 0) {
                     setValue("assigned_meal_plan_ids", preSelectedIds, { shouldValidate: true, shouldDirty: true });
                 }
            }
            if (mealPlansData.data.length > 0) {
                setHasAutoSelected(true);
            }
        }
    }, [isLoading, mealPlansData, memberId, hasAutoSelected, setValue, assignedIds.length]);


    const handleSelect = (planId: number) => {
        if (!planId) return;

        const currentIds = getValues("assigned_meal_plan_ids") || [];
        let newIds = [...currentIds];
        
        if (newIds.includes(planId)) {
            newIds = newIds.filter(id => id !== planId);
        } else {
            newIds.push(planId);
        }
        
        newIds = Array.from(new Set(newIds)).filter(id => typeof id === 'number' && !isNaN(id));
        setValue("assigned_meal_plan_ids", newIds, { shouldValidate: true, shouldDirty: true });
    };

    const getVisibilityLabel = (value: string) => {
        return visibleFor.find(v => v.value === value)?.label || value;
    };
    
    // Pagination Hooks
    const totalRecords = mealPlansData?.filtered_counts || 0;
    const {
        handleLimitChange,
        handleNextPage,
        handlePrevPage,
        handleFirstPage,
        handleLastPage,
        isLastPage,
    } = usePagination<SearchCriteriaType>({
        totalRecords,
        searchCriteria,
        setSearchCriteria,
    });

    const [historyData, setHistoryData] = useState<HistoryConsultation | null>(null);

    return (
        <div className="space-y-4 h-full flex flex-col">
            <HistoryFilter 
                memberId={memberId!} 
                onDataFound={setHistoryData} 
            />

            {historyData && historyData.meal_plans && historyData.meal_plans.length > 0 && (
                <Card className="p-4 border-[#e2e8f0]">
                    <div className="flex items-center justify-between mb-3">
                        <p className="font-semibold text-sm text-[#1e293b]">Previous Meal Plans</p>
                        <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 uppercase">
                            Historical View
                        </span>
                    </div>
                    <div className="flex flex-wrap gap-2">
                        {historyData.meal_plans.map((plan) => (
                            <Badge key={plan.id} variant="outline" className="bg-[#f8fafc] border-[#f1f5f9] text-[#1e293b] font-medium py-1">
                                {plan.meal_plan_name}
                            </Badge>
                        ))}
                    </div>
                </Card>
            )}

            <Card className="p-4 flex flex-col h-full bg-white">
            <div className="space-y-4 h-full flex flex-col">
                <div className="flex items-center gap-4 shrink-0">
                 {/* Replaced custom input with TableSearchFilter */}
                 <TableSearchFilter<SearchCriteriaType>
                    setSearchCriteria={setSearchCriteria}
                    inputValue={inputValue}
                    setInputValue={setInputValue}
                    placeHolder="Search by Names"
                  />
                  <Button 
                    type="button" 
                    className="bg-primary text-sm text-black flex items-center gap-1 lg:mb-0 h-8"
                    onClick={() => navigate("/admin/mealplans?open=true")}
                  >
                    <PlusIcon className="size-4" />
                    <span>Create New</span>
                  </Button>
            </div>

            <div className="rounded-none flex-1 flex flex-col">
                <ScrollArea className="flex-1 min-h-[400px]">
                    {isLoading ? (
                        <div className="flex justify-center p-8 text-gray-400">Loading meal plans...</div>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 p-1">
                            {mealPlansData?.data?.map((plan) => {
                             const rawId = plan.meal_plan_id ?? plan.id;
                             const planId = Number(rawId);

                             if (!planId || isNaN(planId)) return null; 

                            const isSelected = assignedIds.includes(planId);
                            
                            return (
                            <Card 
                                key={planId} 
                                className={cn(
                                    "cursor-pointer transition-all hover:border-primary/50",
                                    isSelected ? "border-primary bg-primary/5 ring-1 ring-primary" : "border-gray-200"
                                )}
                                onClick={() => handleSelect(planId)}
                            >
                                <CardContent className="p-4 flex items-center justify-between gap-3">
                                    <div className="flex items-center gap-3">
                                         {/* Image */}
                                         {plan.profile_img ? (
                                            <img src={plan.profile_img} alt={plan.meal_plan_name} className="w-12 h-12 rounded-full object-cover bg-gray-100" />
                                        ) : (
                                            <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center text-xs text-gray-400">
                                                No Img
                                            </div>
                                        )}
                                        
                                        {/* Details */}
                                        <div className="flex flex-col">
                                            <h4 className="font-semibold text-sm text-gray-900 truncate max-w-[150px]">{plan.meal_plan_name}</h4>
                                            <p className="text-[10px] text-gray-500">
                                                Carbs {plan.carbs}% - Protein {plan.protein}% - Fats {plan.fats}%
                                            </p>
                                        </div>
                                    </div>
                                    
                                    {/* Visibility Badge */}
                                    <Badge 
                                        variant="secondary" 
                                        className={cn(
                                            "text-[10px] font-normal whitespace-nowrap px-2 py-0.5 h-6",
                                            plan.visible_for === "myself" 
                                                ? "bg-green-100 text-green-700 hover:bg-green-200" 
                                                : "bg-blue-100 text-blue-700 hover:bg-blue-200"
                                        )}
                                    >
                                        {getVisibilityLabel(plan.visible_for || "")}
                                    </Badge>
                                </CardContent>
                            </Card>
                        )})}
                        {(!mealPlansData?.data || mealPlansData.data.length === 0) && (
                            <div className="col-span-full text-center p-8 text-gray-400 text-sm">
                                No meal plans found.
                            </div>
                        )}
                    </div>
                )}
            </ScrollArea>
            </div>
            
            {/* Pagination Controls */}
             {(mealPlansData?.filtered_counts || 0) > 0 && (
                <div className="shrink-0 pt-2 border-t mt-auto">
                    <Pagination
                      limit={searchCriteria.limit}
                      offset={searchCriteria.offset}
                      totalItems={totalRecords}
                      onLimitChange={handleLimitChange}
                      onNextPage={handleNextPage}
                      onPrevPage={handlePrevPage}
                      onFirstPage={handleFirstPage}
                      onLastPage={handleLastPage}
                      isLastPage={isLastPage}
                    />
                </div>
              )}
            </div>
        </Card>
        </div>
    );
};

export default MealPlanStep;
